import {
    Div,
    type DivProps,
    type ResponsiveProp,
    Span,
    type UNSAFE_HeightValue,
    type UNSAFE_WidthValue
} from "@hopper-ui/components";
import { clsx } from "clsx";
import type { Ref } from "react";
import { SnapshotConfig } from "@sg-protect/core";
import { useComponentsTranslation } from "../i18n/i18next.ts";
import styles from "./Skeleton.module.css";

export interface SkeletonProps extends Omit<DivProps, "height" | "width" | "ref"> {
    /** This component accepts unsafe values as height. */
    height?: ResponsiveProp<UNSAFE_HeightValue>;
    /**This component accepts unsafe values as width. */
    width?: ResponsiveProp<UNSAFE_WidthValue>;
    message?: string;
    /** Renders a `<span>` instead of a `<div>`, for placement inside inline content like a Paragraph. */
    isInline?: boolean;
    ref?: Ref<HTMLDivElement | HTMLSpanElement>;
}

export const Skeleton = (props: SkeletonProps) => {
    const {
        message,
        borderRadius = "rounded-lg",
        width = "initial",
        height = "initial",
        className,
        isInline = false,
        ref,
        ...others
    } = props;
    const { t } = useComponentsTranslation();

    const sharedProps = {
        borderRadius,
        className: clsx(styles["skeleton"], SnapshotConfig.animationsDisabled && styles["skeletonStatic"], className),
        overflow: "hidden",
        zIndex: 0,
        UNSAFE_width: width,
        UNSAFE_height: height,
        "aria-busy": "true",
        style: { textIndent: "-9999px" },
        UNSAFE_fontSize: "0px",
        "aria-label": others["aria-label"] || t("Skeleton.label"),
        role: others.role || "status"
    } as const;

    return isInline ? (
        <Span {...sharedProps} {...others} ref={ref}>
            {message}
        </Span>
    ) : (
        <Div {...sharedProps} {...others} ref={ref as Ref<HTMLDivElement>}>
            {message}
        </Div>
    );
};
